import PageOverlay from './PageOverlay';
import CloseButton from './CloseButton';
import { useContext, useState } from 'react';
import PortfolioItemCloseBlockingContext from '../../PortfolioItemCloseBlockingContext';
import Dialog from '@/components/Dialog';
import { IconButton, useTheme } from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import UnsavedChangesDialogTitle from './UnsavedChangesDialogTitle';
import type React from 'react';
import RequestPortfolioItemCloseContext from './RequestPortfolioItemCloseContext';
import Space from '@/components/Space';

type ClosablePageOverlayProps = {
  variant: 'challenge' | 'task';
  taskBoardOpen: boolean;
  onClose: () => void;
  onCloseTaskBoard: (() => void) | undefined;
  children: React.ReactNode;
};

const ClosablePageOverlay = ({
  variant,
  taskBoardOpen,
  onClose,
  onCloseTaskBoard,
  children,
}: ClosablePageOverlayProps) => {
  const { isPortfolioItemCloseBlocked, saveAllPendingPortfolioItemChanges } =
    useContext(PortfolioItemCloseBlockingContext);

  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);
  const [saving, setSaving] = useState(false);

  const handleClose = (): void => {
    if (taskBoardOpen) {
      onCloseTaskBoard?.();

      return;
    }

    if (!isPortfolioItemCloseBlocked) {
      onClose();

      return;
    }

    setUnsavedChangesDialogOpen(true);
  };

  const closeUnsavedChangesDialog = (): void => {
    if (!saving) {
      setUnsavedChangesDialogOpen(false);
    }
  };

  const discardPendingChangesAndClose = (): void => {
    onClose();
  };

  const savePendingChangesAndClose = async (): Promise<void> => {
    setSaving(true);

    await saveAllPendingPortfolioItemChanges();

    setUnsavedChangesDialogOpen(false);
    onClose();

    setSaving(false);
  };

  const theme = useTheme();

  return (
    <RequestPortfolioItemCloseContext.Provider value={handleClose}>
      <PageOverlay onClick={handleClose}>
        <CloseButton taskBoardOpen={taskBoardOpen} onClick={handleClose} />
      </PageOverlay>
      <Dialog
        open={unsavedChangesDialogOpen}
        onClose={closeUnsavedChangesDialog}
      >
        <UnsavedChangesDialogTitle>
          <WarningIcon color="warning" />
          <Space width={12} />
          <FormattedMessage id="portfolio.challenge.unsaved_changes_dialog.title" />
          <IconButton onClick={closeUnsavedChangesDialog} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </UnsavedChangesDialogTitle>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedMessageWithChallengeNaming
              id={`portfolio.challenge.unsaved_changes_dialog.text.${variant}`}
            />
          </Dialog.ContentText>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.Button
            variant={theme.palette.isHighContrast ? 'outlined' : 'contained'}
            onClick={discardPendingChangesAndClose}
            disabled={saving}
          >
            <FormattedMessage id="portfolio.challenge.unsaved_changes_dialog.actions.discard_changes" />
          </Dialog.Actions.Button>
          <Dialog.Actions.SaveButton
            loading={saving}
            loadingPosition="start"
            onClick={savePendingChangesAndClose}
          >
            <FormattedMessage
              id={`portfolio.challenge.unsaved_changes_dialog.actions.save_changes.label${saving ? '.saving' : ''}`}
            />
          </Dialog.Actions.SaveButton>
        </Dialog.Actions>
      </Dialog>
      {children}
    </RequestPortfolioItemCloseContext.Provider>
  );
};

export default ClosablePageOverlay;
